import { format } from 'd3-format';
import React, { type ReactElement } from 'react';

import {
  type DivergingBarData,
  NEGATIVE_COLOR,
  OTHER_COLOR,
  POSITIVE_COLOR,
  PRIMARY_COLOR,
} from '@revelio/replots';

import {
  type BreakdownSingleDataSet,
  DataType,
  type SingleDataSet,
} from '../../DataProvider/types';
import type {
  ComparisonBaseline,
  PositionedItem,
} from '../../PageConfigProvider/types/item';

const formatPercent0 = format('.0%');

type TooltipContent = (
  data: DivergingBarData,
  side: 'left' | 'right'
) => ReactElement;

type ValueLabel = (data: DivergingBarData, side: 'left' | 'right') => string;

export type DivergingBarConfig = {
  divergingBarData: DivergingBarData[];
  leftColor: string | undefined;
  rightColor: string | undefined;
  leftFooterLabel: string | undefined;
  rightFooterLabel: string | undefined;
  tooltipContent: TooltipContent | undefined;
  valueLabel: ValueLabel | undefined;
};

const DEFAULT_LEFT_COLOR = '#5CA9E9';
const DEFAULT_RIGHT_COLOR = '#70CA97';

const emptyConfig = (): DivergingBarConfig => ({
  divergingBarData: [],
  leftColor: undefined,
  rightColor: undefined,
  leftFooterLabel: undefined,
  rightFooterLabel: undefined,
  tooltipContent: undefined,
  valueLabel: undefined,
});

const centeredTooltip = ({
  title,
  line,
}: {
  title: string;
  line: ReactElement;
}): ReactElement =>
  React.createElement(
    'div',
    { style: { textAlign: 'center' } },
    React.createElement(
      'div',
      { style: { fontWeight: 'bold', marginBottom: '4px' } },
      title
    ),
    line
  );

export type GetDivergingBarConfigOptions = {
  /** Which entity is the baseline (comparison/reference), shown left in grey. The other is the focus, shown right in blue. */
  baseline?: ComparisonBaseline;
  /** Use focus=blue (right), baseline=grey (left). For combo dual view. */
  dualViewColors?: boolean;
};

export const getDivergingBarConfig = (
  dataSet: BreakdownSingleDataSet | SingleDataSet,
  item: PositionedItem | undefined,
  colorMap: Record<string, string> | undefined,
  formatValue: (value: number) => string,
  options?: GetDivergingBarConfigOptions
): DivergingBarConfig => {
  if (dataSet.type === DataType.BREAKDOWN_SINGLE) {
    if (dataSet.data.length !== 2) return emptyConfig();

    const [entity1, entity2] = dataSet.data;
    if (!entity1 || !entity2) return emptyConfig();

    const baseline = options?.baseline ?? 'second';
    const leftEntity = baseline === 'first' ? entity1 : entity2;
    const rightEntity = baseline === 'first' ? entity2 : entity1;
    // Left = baseline (comparison, grey). Right = focus (blue).

    const leftBreakdown = leftEntity.breakdown;
    const rightBreakdown = rightEntity.breakdown;

    const allDims = new Set<string>();
    // biome-ignore lint/suspicious/useIterableCallbackReturn: migrated from develop
    leftBreakdown.forEach((b) => allDims.add(b.dim));
    // biome-ignore lint/suspicious/useIterableCallbackReturn: migrated from develop
    rightBreakdown.forEach((b) => allDims.add(b.dim));

    const rows: DivergingBarData[] = Array.from(allDims).map((dim) => {
      const leftValue = leftBreakdown.find((b) => b.dim === dim);
      const rightValue = rightBreakdown.find((b) => b.dim === dim);

      return {
        label: dim,
        leftValue: -(leftValue?.share ?? 0),
        rightValue: rightValue?.share ?? 0,
        leftLabel: leftEntity.dim,
        rightLabel: rightEntity.dim,
        leftId: leftValue?.id ?? null,
        rightId: rightValue?.id ?? null,
      };
    });

    const leftColorKey = `${leftEntity.dim}`;
    const rightColorKey = `${rightEntity.dim}`;

    const resolvedLeftColor = (() => {
      if (options?.dualViewColors) return OTHER_COLOR;
      return (
        colorMap?.[leftColorKey] ??
        item?.plot?.colorPalette?.[0] ??
        DEFAULT_LEFT_COLOR
      );
    })();
    const resolvedRightColor = (() => {
      if (options?.dualViewColors) return PRIMARY_COLOR;
      return (
        colorMap?.[rightColorKey] ??
        item?.plot?.colorPalette?.[1] ??
        DEFAULT_RIGHT_COLOR
      );
    })();

    const rawValueLabel: ValueLabel = (d, side) => {
      const value = side === 'left' ? d.leftValue : d.rightValue;
      return formatValue(Math.abs(value ?? 0));
    };

    const rawTooltipContent: TooltipContent = (d, side) => {
      const value = side === 'left' ? d.leftValue : d.rightValue;
      const label = side === 'left' ? d.leftLabel : d.rightLabel;

      return centeredTooltip({
        title: d.label,
        line: React.createElement(
          'div',
          null,
          label,
          ': ',
          formatValue(Math.abs(value ?? 0))
        ),
      });
    };

    return {
      divergingBarData: rows,
      leftColor: resolvedLeftColor,
      rightColor: resolvedRightColor,
      leftFooterLabel: leftEntity.dim,
      rightFooterLabel: rightEntity.dim,
      tooltipContent: rawTooltipContent,
      valueLabel: rawValueLabel,
    };
  }

  const rawNegativeLabel = item?.plot?.divergingBar?.negativeLabel;
  const rawPositiveLabel = item?.plot?.divergingBar?.positiveLabel;
  const configuredLeftLabel =
    rawNegativeLabel && rawNegativeLabel.trim().length > 0
      ? rawNegativeLabel
      : undefined;
  const configuredRightLabel =
    rawPositiveLabel && rawPositiveLabel.trim().length > 0
      ? rawPositiveLabel
      : undefined;

  const isComparisonTransformationEnabled =
    item?.plot?.comparisonTransformation !== undefined;

  const rows: DivergingBarData[] = dataSet.data.map((point) => {
    const v = point.value ?? 0;
    const count = (point as unknown as { count?: number }).count;
    const { leftValue, rightValue } = (() => {
      if (!isComparisonTransformationEnabled) {
        if (v < 0) return { leftValue: v, rightValue: null };
        if (v > 0) return { leftValue: null, rightValue: v };
        // When the value is exactly 0, show 0.0 on both sides.
        return { leftValue: 0, rightValue: 0 };
      }

      if (v < 0) return { leftValue: v, rightValue: null };
      if (v > 0) return { leftValue: null, rightValue: v };

      // Comparison mode: show only the positive 0 bar/label.
      return { leftValue: null, rightValue: 0 };
    })();
    const row: DivergingBarData & { count?: number } = {
      label: point.dim,
      leftValue,
      rightValue,
      leftLabel: configuredLeftLabel ?? '',
      rightLabel: configuredRightLabel ?? '',
      leftId: point.id ?? null,
      rightId: point.id ?? null,
      count: count ?? undefined,
    };
    return row;
  });

  const transformedValueLabel: ValueLabel = (d, side) => {
    const value = side === 'left' ? d.leftValue : d.rightValue;
    const n = value ?? 0;
    return formatValue(n);
  };

  const transformedTooltipContent: TooltipContent = (d, side) => {
    const value = side === 'left' ? d.leftValue : d.rightValue;
    const count = (d as DivergingBarData & { count?: number }).count;

    if (count !== undefined && count !== null) {
      return React.createElement(
        'div',
        null,
        React.createElement(
          'div',
          { style: { fontWeight: 'bold', marginBottom: '4px' } },
          `${d.label}: ${formatPercent0(value ?? 0)}`
        ),
        React.createElement('div', null, `(n: ${count})`)
      );
    }

    const n = value ?? 0;
    const diffText = formatValue(n);

    return centeredTooltip({
      title: d.label,
      line: React.createElement('div', null, 'Difference: ', diffText),
    });
  };

  return {
    divergingBarData: rows,
    leftColor: NEGATIVE_COLOR,
    rightColor: POSITIVE_COLOR,
    leftFooterLabel: configuredLeftLabel,
    rightFooterLabel: configuredRightLabel,
    tooltipContent: transformedTooltipContent,
    valueLabel: transformedValueLabel,
  };
};
